// In-flight archive jobs survive a reload, a closed tab and a closed app via
// localStorage, so a returning user gets the finished download instead of
// nothing. The worker keeps building regardless of the browser; all that is
// stored here is what is needed to poll the job again.
const STORAGE_KEY = 'projectBankArchiveJobs';

// The job marker the poll reads lives about a day on the backend (the
// temp-downloads/ lifecycle rule), so anything older cannot be resolved and
// is dropped rather than polled.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const readAll = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    // localStorage can be unavailable or blocked; resuming is best-effort.
    return {};
  }
};

const writeAll = (jobs) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  } catch (error) {
    // Best-effort only, see above.
  }
};

/**
 * @param {object} job
 * @param {string} job.archiveJobId
 * @param {string} job.projectId
 * @param {string} [job.projectName]
 * @param {string} [job.projectKey] route key of the project email
 * @param {'DOWNLOAD'|'PROJECT_EMAIL'} [job.delivery] what the finished job
 *   is offered as; DOWNLOAD when absent
 * @param {boolean} [job.emailScheduled] PROJECT_EMAIL: the message has been
 *   handed to the backend, which sends it when the archive is ready
 * @param {number} job.startedAt epoch ms; the poll budget counts from it
 */
export const saveStoredArchiveJob = (job) => {
  const all = readAll();
  all[job.archiveJobId] = job;
  writeAll(all);
};

/** Patches one stored job; a job no longer stored is left alone. */
export const updateStoredArchiveJob = (archiveJobId, patch) => {
  const all = readAll();
  if (all[archiveJobId]) {
    all[archiveJobId] = { ...all[archiveJobId], ...patch };
    writeAll(all);
  }
};

export const clearStoredArchiveJob = (archiveJobId) => {
  const all = readAll();
  if (all[archiveJobId]) {
    delete all[archiveJobId];
    writeAll(all);
  }
};

/** Stored jobs still young enough to resolve, oldest first; stale ones are pruned. */
export const readStoredArchiveJobs = () => {
  const all = readAll();
  const now = Date.now();
  const fresh = Object.values(all)
    .filter(
      (job) =>
        job?.archiveJobId &&
        job.projectId &&
        now - (job.startedAt || 0) < MAX_AGE_MS
    )
    .sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));

  if (fresh.length !== Object.keys(all).length) {
    writeAll(Object.fromEntries(fresh.map((job) => [job.archiveJobId, job])));
  }

  return fresh;
};
